import React, { useEffect, useState, useRef } from "react";
import {
  TextField,
  MenuItem,
  Box,
  Typography,
  FormHelperText,
  Grid,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReCAPTCHA from "react-google-recaptcha";
import ApiCallServices from "../../../../services/ApiCallServices";
import { END_POINTS } from "../../../../services/endpoint/endPoint";

const generateSixDigitNumber = () => {
  const min = 100000;
  const max = 999999;
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

function RequestSegments({ isSubmitted, setIsSubmitted }) {
  const [formData, setFormData] = useState({
    dsp: "",
    seatId: "",
    dealName: "",
    dealFormat: "",
    margin: "",
    cpm: "",
    agencyCode: "",
    phone: "",
    email: "",
    comments: "",
    randNumber: generateSixDigitNumber(),
  });

  const [csvFile, setCsvFile] = useState(null);
  const [parsedUrls, setParsedUrls] = useState([]);
  const [errors, setErrors] = useState({});
  const [csvError, setCsvError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "comments" && value.length > 1023) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.dsp) newErrors.dsp = "DSP is required";
    if (!formData.dealName) newErrors.dealName = "Deal Name is required";
    if (!formData.dealFormat) newErrors.dealFormat = "Deal Format is required";
    if (!formData.margin) newErrors.margin = "Curator Margin is required";
    if (!formData.cpm) newErrors.cpm = "CPM is required";
    if (!formData.agencyCode) newErrors.agencyCode = "Agency Code is required";
    if (!formData.phone) newErrors.phone = "Phone is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!parsedUrls.length) newErrors.csv = "CSV file is required";
    if (!recaptchaToken) newErrors.recaptcha = "Please verify CAPTCHA";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (isSubmitted && recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  }, [isSubmitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    const payload = {
      dsp: formData.dsp,
      seat_id: formData.seatId,
      dealName: formData.dealName,
      dealFormat: formData.dealFormat,
      margin: formData.margin,
      cpm: formData.cpm,
      agencyCode: formData.agencyCode,
      phone_no: formData.phone,
      email: formData.email,
      comments: formData.comments,
      recaptcha_token: recaptchaToken,
      randNumber: formData.randNumber,
      url_list: parsedUrls, // ✅ parsed CSV URLs
    };

    try {
      const res = await ApiCallServices.create(
        END_POINTS.createSegmentRequest,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (res?.status === 200) {
        setIsSubmitted(true);
        setCsvFile(null);
        setParsedUrls([]);
        setRecaptchaToken(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const downloadCsvTemplate = () => {
    const csvContent = "url\nhttps://example.com\nhttps://example2.com\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "url_list_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isSubmitted) {
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <CheckCircleIcon sx={{ color: "green", fontSize: 60, mb: 2 }} />
        <Typography variant="h5">Thank you!</Typography>
        <Typography sx={{ mt: 1 }}>
          Your deal will be pushed to your DSP within 24 hours.
        </Typography>
        <Typography>Email notification will be sent to:</Typography>
        <Typography fontWeight={600}>{formData.email}</Typography>
        <Typography sx={{ mt: 2 }} variant="h6">
          Reference Code: {formData.randNumber}
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 900, mx: "auto", p: 3 }}>
      <Grid container spacing={2}>

        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Select DSP"
            name="dsp"
            value={formData.dsp}
            onChange={handleChange}
            error={!!errors.dsp}
            helperText={errors.dsp}
            required
          >
            <MenuItem value="1">DV360</MenuItem>
            <MenuItem value="2">TTD</MenuItem>
            <MenuItem value="3">Amazon DSP</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Seat ID" name="seatId" value={formData.seatId} onChange={handleChange} />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Deal Name" name="dealName" value={formData.dealName} onChange={handleChange} required />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Deal Format"
            name="dealFormat"
            value={formData.dealFormat}
            onChange={handleChange}
            error={!!errors.dealFormat}
            helperText={errors.dealFormat}
            required
          >
            <MenuItem value="1">Display</MenuItem>
            <MenuItem value="2">Video</MenuItem>
            <MenuItem value="3">CTV</MenuItem>
            <MenuItem value="4">All</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Curator Margin %" type="number" name="margin" value={formData.margin} onChange={handleChange} required />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Buying CPM" type="number" name="cpm" value={formData.cpm} onChange={handleChange} required />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Agency Code" name="agencyCode" value={formData.agencyCode} onChange={handleChange} required />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Phone" name="phone" value={formData.phone} onChange={handleChange} error={!!errors.phone} helperText={errors.phone} required />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Email" type="email" name="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} required />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="body2">Upload URL List (CSV only)</Typography>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => {
              const file = e.target.files[0];
              if (!file) return;

              if (!file.name.endsWith(".csv")) {
                setCsvError("Only CSV files are allowed");
                setCsvFile(null);
                setParsedUrls([]);
                return;
              }

              const reader = new FileReader();

              reader.onload = (event) => {
                const text = event.target.result;
                const lines = text.split(/\r?\n/);

                const urls = lines
                  .slice(1)
                  .map((line) => line.trim())
                  .filter((line) => line !== "");

                setParsedUrls(urls);
                setCsvFile(file);
                setCsvError("");
              };

              reader.onerror = () => {
                setCsvError("Failed to read CSV file");
              };

              reader.readAsText(file);
            }}
          />

          {csvFile && (
            <FormHelperText sx={{ color: "green" }}>
              {csvFile.name} ({parsedUrls.length} URLs parsed)
            </FormHelperText>
          )}

          {(errors.csv || csvError) && (
            <FormHelperText error>
              {errors.csv || csvError}
            </FormHelperText>
          )}

          <Button size="small" sx={{ mt: 1 }} onClick={downloadCsvTemplate}>
            Download CSV Template
          </Button>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comments"
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            inputProps={{ maxLength: 1023 }}
            helperText={`${formData.comments.length}/1023 characters`}
          />
        </Grid>

        <Grid item xs={12}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={
              process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
              "6LfllzkrAAAAABbh3VRGDh1j2y5daFE_4YhFlmuf"
            }
            onChange={(token) => setRecaptchaToken(token)}
            onExpired={() => setRecaptchaToken(null)}
          />
          {errors.recaptcha && <FormHelperText error>{errors.recaptcha}</FormHelperText>}
        </Grid>

        <Grid item xs={12} textAlign="right">
          <Button type="submit" variant="contained">
            Submit
          </Button>
        </Grid>

      </Grid>
    </Box>
  );
}

export default RequestSegments;
